(() => {
  if (window.JFProductCardRotation) return;

  const configElement = document.getElementById('JFProductCardMotionConfig');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent || '{}');
  } catch (error) {
    console.error('[JF Product Cards] Invalid motion configuration.', error);
    return;
  }

  const settings = {
    enabled: config.enabled !== false,
    interval: Math.max(2000, Number(config.interval) || 3000),
    duration: Math.max(200, Number(config.duration) || 400),
    pauseOnHover: config.pauseOnHover !== false,
    respectReducedMotion: config.respectReducedMotion !== false
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const states = new WeakMap();
  const productCache = new Map();

  const normalizeImageUrl = (value, width) => {
    if (!value) return '';
    const raw = value.startsWith('//') ? `${window.location.protocol}${value}` : value;
    const url = new URL(raw, window.location.origin);
    if (width) url.searchParams.set('width', width);
    return url.toString();
  };

  const productJsonUrl = (href) => {
    const url = new URL(href, window.location.origin);
    url.hash = '';
    url.search = '';
    url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    return url.toString();
  };

  const getProductImages = (href) => {
    const endpoint = productJsonUrl(href);
    if (!productCache.has(endpoint)) {
      productCache.set(endpoint,
        fetch(endpoint, { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error(`Product media request failed: ${response.status}`);
            return response.json();
          })
          .then((product) => Array.isArray(product.images) ? product.images : [])
          .catch((error) => {
            console.error('[JF Product Cards]', error);
            return [];
          })
      );
    }
    return productCache.get(endpoint);
  };

  const getElements = (card) => {
    const isJFCard = card.matches('[data-jf-collection-card]');
    const frame = isJFCard
      ? card.querySelector('.jf-collection-card__media-link')
      : card.querySelector('.card__media .media');
    const primary = isJFCard
      ? card.querySelector('.jf-collection-card__primary')
      : frame?.querySelector('img');
    const link = isJFCard
      ? card.querySelector('.jf-collection-card__media-link[href]')
      : card.querySelector('.card__heading a[href*="/products/"], a[id^="CardLink-"][href]');
    return { frame, primary, link };
  };

  const createLayer = (state, source, index) => {
    if (state.layers[index]) return state.layers[index];
    const image = document.createElement('img');
    image.className = 'jf-card-rotator-layer';
    image.alt = '';
    image.decoding = 'async';
    image.loading = 'lazy';
    image.sizes = state.primary.getAttribute('sizes') || '(min-width: 990px) 25vw, 50vw';
    image.srcset = [180, 260, 360, 480, 600, 720, 900]
      .map((width) => `${normalizeImageUrl(source, width)} ${width}w`)
      .join(', ');
    image.src = normalizeImageUrl(source, 720);
    state.frame.appendChild(image);
    state.layers[index] = image;
    return image;
  };

  const preloadIndex = async (state, index) => {
    if (!state.sources.length || index === 0) return;
    const sourceIndex = index - 1;
    const image = createLayer(state, state.sources[sourceIndex], sourceIndex);
    try {
      if (image.decode) await image.decode();
    } catch (_) {
      /* Browser will still use the normal image load path. */
    }
  };

  const showIndex = (state, nextIndex) => {
    if (nextIndex === state.index) return;
    const previousIndex = state.index;

    if (nextIndex === 0) {
      state.primary.classList.remove('is-rotated-out');
    } else {
      const nextLayer = state.layers[nextIndex - 1];
      if (nextLayer) nextLayer.classList.add('is-active');
      state.primary.classList.add('is-rotated-out');
    }

    if (previousIndex > 0) {
      const previousLayer = state.layers[previousIndex - 1];
      if (previousLayer && previousIndex !== nextIndex) previousLayer.classList.remove('is-active');
    }

    state.index = nextIndex;
  };

  const shouldReduceMotion = () => settings.respectReducedMotion && reducedMotion.matches;

  const clearTimer = (state) => {
    if (state.timer) window.clearTimeout(state.timer);
    state.timer = null;
  };

  const schedule = (state) => {
    clearTimer(state);
    if (!state.visible || state.hovered || state.sources.length === 0 || shouldReduceMotion()) return;

    const nextIndex = (state.index + 1) % (state.sources.length + 1);
    preloadIndex(state, nextIndex);
    state.timer = window.setTimeout(async () => {
      if (!state.visible || state.hovered || shouldReduceMotion()) return;
      await preloadIndex(state, nextIndex);
      showIndex(state, nextIndex);
      schedule(state);
    }, settings.interval);
  };

  const activate = async (state) => {
    if (state.loaded || state.loading || shouldReduceMotion()) {
      if (state.loaded) schedule(state);
      return;
    }
    state.loading = true;
    const images = await getProductImages(state.link.href);
    state.loading = false;
    if (!state.visible || images.length < 2) return;

    const primaryUrl = normalizeImageUrl(state.primary.currentSrc || state.primary.src);
    const unique = [];
    images.forEach((source) => {
      const normalized = normalizeImageUrl(source);
      if (!normalized) return;
      const comparable = normalized.split('?')[0];
      if (primaryUrl.split('?')[0] === comparable) return;
      if (!unique.some((item) => item.split('?')[0] === comparable)) unique.push(normalized);
    });

    if (!unique.length) return;
    state.sources = unique;
    state.loaded = true;
    state.card.classList.add('jf-card-rotation-enabled');
    state.card.classList.toggle('jf-card-respect-reduced-motion', settings.respectReducedMotion);
    state.frame.classList.add('jf-card-rotator-frame');
    state.frame.style.setProperty('--jf-card-rotation-duration', `${settings.duration}ms`);
    state.primary.classList.add('jf-card-rotator-primary');
    schedule(state);
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const state = states.get(entry.target);
      if (!state) return;
      state.visible = entry.isIntersecting && entry.intersectionRatio > 0;
      if (state.visible) activate(state);
      else clearTimer(state);
    });
  }, { threshold: 0.08 });

  const initCard = (card) => {
    if (!settings.enabled || states.has(card)) return;
    const { frame, primary, link } = getElements(card);
    if (!frame || !primary || !link) return;

    const state = {
      card,
      frame,
      primary,
      link,
      sources: [],
      layers: [],
      index: 0,
      visible: false,
      hovered: false,
      loading: false,
      loaded: false,
      timer: null
    };
    states.set(card, state);

    if (settings.pauseOnHover) {
      card.addEventListener('mouseenter', () => {
        state.hovered = true;
        clearTimer(state);
      }, { passive: true });
      card.addEventListener('mouseleave', () => {
        state.hovered = false;
        schedule(state);
      }, { passive: true });
    }

    observer.observe(card);
  };

  const init = (scope = document) => {
    if (!(scope instanceof Element || scope === document)) return;
    if (scope instanceof Element && scope.matches('.card-wrapper, [data-jf-collection-card]')) initCard(scope);
    scope.querySelectorAll?.('.card-wrapper, [data-jf-collection-card]').forEach(initCard);
  };

  const observeDynamicRoot = (root) => {
    if (!root || root.dataset.jfRotationObserved === 'true') return;
    root.dataset.jfRotationObserved = 'true';
    const dynamicObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) init(node);
        }
      }
    });
    dynamicObserver.observe(root, { childList: true, subtree: true });
  };

  const initDynamicRoots = (scope = document) => {
    scope.querySelectorAll?.('#ProductGridContainer, product-recommendations').forEach(observeDynamicRoot);
  };

  const resetReducedMotion = () => {
    document.querySelectorAll('.jf-card-rotation-enabled').forEach((card) => {
      const state = states.get(card);
      if (!state) return;
      if (shouldReduceMotion()) {
        clearTimer(state);
        showIndex(state, 0);
      } else if (state.visible) {
        schedule(state);
      }
    });
  };

  if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', resetReducedMotion);

  window.JFProductCardRotation = { init };

  const boot = () => {
    init(document);
    initDynamicRoots(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    initDynamicRoots(event.target);
  });
})();
